import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import type { SplitPaymentLaunchContext } from "../../types/paymentLinksPanel";
import Header from "../../layouts/Header";
import Toaster from "../../components/ui/toaster";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Skeleton } from "../../components/ui/skeleton";
import { useAuth } from "../../context/AuthContext";
import { useCurrencySelection } from "../../context/CurrencySelectionContext";
import {
  useActiveAccount,
  useQueryUserProfile,
} from "../../hooks/query/queries";
import {
  useBusinessAvailableAssets,
  useBusinessWalletAssetsData,
  useBusinessWalletBalance,
} from "../../hooks/query/businessWalletQueries";
import { useSendFlowState } from "../../hooks/useSendFlowState";
import CurrencyFormatter from "../../services/currencyFormatterService";
import { setShowBalances } from "../../services/settingsService";
import { useToast } from "../../hooks/use-toast";
import BusinessSuiteHero from "../../components/BusinessSuiteHero";
import BusinessSuiteSideNav from "../../components/BusinessSuiteSideNav";
import DPOSPage from "../../components/dpos/DPOSPage";
import MarketDashboard from "../../components/MarketDashboard";
import BuySellPage from "../../components/BuySellPage";
import BuySellPageSkeleton from "../../components/skeletons/BuySellPageSkeleton";
import BuySellCarousel from "../../components/BuySellCarousel";
import WithdrawParent from "../../components/WithdrawParent";
import SendReceivePage from "../../components/SendReceivePage";
import EightyEightPay from "../../components/EightyEightPay";
import PaymentLinksPanel from "../../components/PaymentLinksPanel";
import AddressBookPage from "../../components/AddressBookPage";
import { usePairStore } from "../../store/useStore";
import {
  useCryptoAssets,
  useFetchAllTradablePairs,
  useFeatureFlagsByCategory,
  useNetworksByAssets,
} from "../../hooks/query/queries";
import { useActiveBusiness } from "../../hooks/query/dposQueries";
import { findMatchingTradingAsset } from "../../utils/tradingAssetMatch";
import { mergeTradableWithBusinessWallet } from "../../utils/mergeTradableWithBusinessWallet";
import CryptoAssetComponentDashboardBase from "../../components/CryptoAssetsDashboard";
import BusinessSuiteTransactionHistoryBase from "../../components/BusinessSuiteTransactionHistory";
import BusinessSuitePerformanceCardBase from "../../components/BusinessSuitePerformanceCard";

const CryptoAssetComponentDashboard =
  CryptoAssetComponentDashboardBase as React.ComponentType<{
    styleClasses?: string;
    fromDashboard?: boolean;
    businessSuiteAssetNav?: boolean;
    businessId?: string | null;
  }>;

const BusinessSuitePerformanceCard =
  BusinessSuitePerformanceCardBase as React.ComponentType<{
    businessId?: string | null;
  }>;

const BusinessSuiteTransactionHistory =
  BusinessSuiteTransactionHistoryBase as React.ComponentType<{
    assetFocusView?: boolean;
    businessId?: string | null;
    onSplitPayment?: (ctx: SplitPaymentLaunchContext | null) => void;
  }>;
interface WalletAsset {
  id?: string;
  _id?: string;
  assetId?: string;
  asset?: string;
  assetName?: string;
  image?: string;
  symbol?: string;
  availableAmount?: number | string;
  currencyType?: string;
  type?: string;
}

interface LocationState {
  parentNavTab?: string;
  parentTab?: string;
  tabActive?: string;
  selectedCrypto?: WalletAsset;
  type?: string;
  selectedTab?: string;
  childTab?: string;
}

const BUSINESS_SUITE_TABS_WITH_URL = [
  "overview",
  "dpos",
  "market",
  "buy-sell",
  "send-receive",
  "withdraw-deposit",
  "80eightpay",
  "pay-link",
  "address-book",
] as const;

type BusinessSuiteUrlTab = (typeof BUSINESS_SUITE_TABS_WITH_URL)[number];

const BUSINESS_SUITE_ZAR_ASSET_CODE = "ZAR" as const;
const BUSINESS_SUITE_ZAR_DISPLAY_NAME = "South African Rand" as const;

function isBusinessSuiteTabWithUrl(tab: string): tab is BusinessSuiteUrlTab {
  return (BUSINESS_SUITE_TABS_WITH_URL as readonly string[]).includes(tab);
}

function isBusinessSuiteZarAsset(
  a: Pick<WalletAsset, "asset" | "assetName">
): boolean {
  return (
    a.asset === BUSINESS_SUITE_ZAR_ASSET_CODE ||
    a.assetName === BUSINESS_SUITE_ZAR_DISPLAY_NAME
  );
}

function findBusinessSuiteZarAsset(
  assets: WalletAsset[] | null | undefined
): WalletAsset | undefined {
  return assets?.find((a) => isBusinessSuiteZarAsset(a));
}

const BUSINESS_SUITE_CURRENCY_FORMATTER = new CurrencyFormatter({
  locale: "en-US",
  decimalPlaces: 2,
  cryptocurrencyDecimalPlaces: 2,
  stablecoins: ["USDT", "USDC", "DAI"],
});

export default function BusinessSuiteDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    parentNavTab,
    parentTab,
    tabActive,
    selectedCrypto,
    type: locationType,
    selectedTab,
    childTab: locationChildTab,
  } = (location.state as LocationState) || {};
  const urlPayId = searchParams.get("payId");
  const urlTab = searchParams.get("tab");
  const assetFocusParam = searchParams.get("asset");

  const { setPairObject } = usePairStore();
  const [, setLoadingAsset] = useState<boolean>(false);
  const { settings, userProfile, updateSettings } = useAuth();
  const { toast } = useToast();
  const {
    business: activeBusiness,
    businessId,
    isLoading: isBusinessContextLoading,
  } = useActiveBusiness();
  const { selectedAsset, setSelectedAsset } = useCurrencySelection();
  const { data: walletData } = useBusinessWalletBalance(businessId);
  const { data: walletAssetsData, isLoading: isLoadingAssets } =
    useBusinessWalletAssetsData(businessId);
  const { data: tradingAssets } = useCryptoAssets();
  const { data: tradingNets } = useNetworksByAssets(
    tradingAssets,
    setLoadingAsset
  );
  const { data: pairObject } = useFetchAllTradablePairs();

  const tradingAssetsWithBusinessBalances = useMemo(() => {
    if (!businessId || !tradingAssets || !Array.isArray(tradingAssets)) {
      return tradingAssets;
    }
    return mergeTradableWithBusinessWallet(
      tradingAssets,
      walletAssetsData?.assets
    );
  }, [businessId, tradingAssets, walletAssetsData?.assets]);
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures") as {
    data?: {
      buyCrypto?: boolean;
      sellCrypto?: boolean;
      deposit?: boolean;
      withdraw?: boolean;
      sendCrypto?: boolean;
      receiveCrypto?: boolean;
      eightPay?: boolean;
    };
  };

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [launchPayLinkWizard, setLaunchPayLinkWizard] = useState(false);
  const [splitPaymentLaunchContext, setSplitPaymentLaunchContext] =
    useState<SplitPaymentLaunchContext | null>(null);
  const [transactionTab, setTransactionTab] = useState<string>("");
  const [asset, setAsset] = useState<WalletAsset | null>(null);
  const [showBuySellCarousel, setShowBuySellCarousel] = useState<boolean>(true);
  const [childTab, setChildTab] = useState<string | undefined>(
    locationChildTab
  );
  const { clearState } = useSendFlowState();

  const userId = userProfile?._id;
  const accountId = userProfile?.subAccountDetails?.[0]?._id;
  const { data: activeAccount } = useActiveAccount(userId, accountId);
  const mainAccount = activeAccount?.isMainAccount;

  const investParams = useMemo(() => ({ id: userId }), [userId]);
  const { data: assetsOwned, isLoading: isLoadingAssetsOwned } =
    useBusinessAvailableAssets({
      businessId,
      isSend: true,
      isEightyeightPay: false,
      investArray: investParams,
      balanceParams: { ...investParams },
      balanceParamsEightEight: investParams,
      enabled: Boolean(businessId),
    });

  const assetsForUse = useMemo(
    () => walletAssetsData?.assets || [],
    [walletAssetsData?.assets]
  );
  const selectedAssetNames = ["USDT", BUSINESS_SUITE_ZAR_DISPLAY_NAME];

  const focusedWalletAsset = useMemo(() => {
    if (!assetFocusParam || assetsForUse.length === 0) {
      return null;
    }
    const code = assetFocusParam.trim().toUpperCase();
    return (
      assetsForUse.find(
        (a: WalletAsset) => (a.asset || "").toUpperCase() === code
      ) || null
    );
  }, [assetFocusParam, assetsForUse]);

  const isAssetFocusView = Boolean(focusedWalletAsset);

  const zarBalance = useMemo(() => {
    const zarAsset = findBusinessSuiteZarAsset(assetsForUse);
    const n = parseFloat(String(zarAsset?.availableAmount ?? 0));
    return Number.isFinite(n) ? n : 0;
  }, [assetsForUse]);

  /** Updates only `tab` in the query string; other params (e.g. `payId`) are kept. */
  const syncTabInUrl = useCallback(
    (tab: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (tab === "overview") {
            p.delete("tab");
          } else {
            p.set("tab", tab);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useQueryUserProfile();

  useEffect(() => {
    if (pairObject) {
      setPairObject(pairObject);
    }
  }, [pairObject, setPairObject]);

  useEffect(() => {
    if (
      Array.isArray(assetsOwned) &&
      assetsOwned.length > 0 &&
      assetsOwned.some((owned) => {
        const o = owned as { availableAmount?: number | string };
        return Number(o?.availableAmount ?? 0) > 0;
      })
    ) {
      setShowBuySellCarousel(false);
    }
  }, [assetsOwned]);

  useEffect(() => {
    if (locationChildTab !== undefined) {
      setChildTab(locationChildTab);
    }
  }, [locationChildTab]);

  useEffect(() => {
    clearState();
  }, [activeTab, clearState]);

  useEffect(() => {
    if (urlPayId && urlTab === "pay") {
      syncTabInUrl("80eightpay");
      setActiveTab("80eightpay");
      return;
    }

    if (urlTab && urlTab !== "pay") {
      if (isBusinessSuiteTabWithUrl(urlTab)) {
        setActiveTab(urlTab);
        return;
      }
      setActiveTab("overview");
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete("tab");
          return p;
        },
        { replace: true }
      );
      return;
    }

    if (parentNavTab && isBusinessSuiteTabWithUrl(parentNavTab)) {
      setActiveTab(parentNavTab);
      if (!urlTab || urlTab !== parentNavTab) {
        syncTabInUrl(parentNavTab);
      }
      return;
    }

    if (locationType && tabActive) {
      setTransactionTab(tabActive);
      setActiveTab(locationType);
      setAsset(selectedCrypto ?? null);
      if (isBusinessSuiteTabWithUrl(locationType)) {
        if (!urlTab || urlTab !== locationType) {
          syncTabInUrl(locationType);
        }
      }
      return;
    }

    if (parentTab) {
      setActiveTab(parentTab);
      if (isBusinessSuiteTabWithUrl(parentTab)) {
        if (!urlTab || urlTab !== parentTab) {
          syncTabInUrl(parentTab);
        }
      }
    }
  }, [
    locationType,
    parentNavTab,
    parentTab,
    selectedCrypto,
    syncTabInUrl,
    tabActive,
    urlPayId,
    urlTab,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!selectedCrypto || locationType !== "buy-sell") {
      return;
    }
    const match = findMatchingTradingAsset(selectedCrypto, tradingAssets);
    if (match) {
      setAsset(match);
    }
  }, [locationType, selectedCrypto, tradingAssets]);

  useEffect(() => {
    if (assetsForUse.length > 0 && !selectedAsset) {
      const zarAsset = findBusinessSuiteZarAsset(assetsForUse);
      setSelectedAsset(zarAsset || assetsForUse[0]);
    }
  }, [assetsForUse, selectedAsset, setSelectedAsset]);

  const handleSelect = (asset: WalletAsset) => {
    setSelectedAsset(asset);
  };

  const handleTabChange = (tabId: string, options?: { childTab?: string }) => {
    clearState();
    setTransactionTab("");
    setAsset(null);
    if (
      tabId !== "buy-sell" &&
      tabId !== "withdraw-deposit" &&
      tabId !== "send-receive"
    ) {
      setChildTab(undefined);
    } else if (options?.childTab !== undefined) {
      setChildTab(options.childTab);
    }
    setActiveTab(tabId);
    syncTabInUrl(tabId);
  };

  const handleSplitPaymentFromTransaction = (
    ctx: SplitPaymentLaunchContext | null
  ) => {
    setSplitPaymentLaunchContext(ctx);
    setLaunchPayLinkWizard(true);
    handleTabChange("pay-link");
  };

  const handleToggleBalanceVisibility = async () => {
    if (!userProfile?._id) return;
    try {
      const apiCall = await setShowBalances(userProfile._id);
      if (apiCall?.status === 200 && apiCall?.data?.status === true) {
        updateSettings(apiCall.data.data);
      } else {
        toast({
          description: "Error updating balance visibility",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        description: "Error updating balance visibility",
        variant: "destructive",
      });
    }
  };

  const formattedPortfolioValue = useMemo(() => {
    if (!walletAssetsData || !selectedAsset) {
      return walletData?.portfolioEstimate || "R 0.00";
    }
    if (settings?.digitalWallet?.showBalances === false) {
      return "****";
    }
    const selectedAssetCode = selectedAsset.asset?.toUpperCase();
    if (selectedAssetCode === "USDT") {
      return BUSINESS_SUITE_CURRENCY_FORMATTER.format(
        walletAssetsData.usdtEstimate || 0,
        "crypto",
        "USDT",
        {
          showSymbol: true,
        }
      );
    }
    return BUSINESS_SUITE_CURRENCY_FORMATTER.format(
      walletAssetsData.zarEstimate || 0,
      "fiat",
      BUSINESS_SUITE_ZAR_ASSET_CODE
    );
  }, [walletAssetsData, selectedAsset, walletData, settings]);

  const assetFocusPrimaryLine = useMemo(() => {
    if (!focusedWalletAsset) {
      return "";
    }
    if (settings?.digitalWallet?.showBalances === false) {
      return "****";
    }
    const code = (focusedWalletAsset.asset || "").toUpperCase();
    if (isBusinessSuiteZarAsset(focusedWalletAsset)) {
      const n = parseFloat(String(focusedWalletAsset.availableAmount || "0"));
      return BUSINESS_SUITE_CURRENCY_FORMATTER.format(
        n,
        "fiat",
        BUSINESS_SUITE_ZAR_ASSET_CODE
      );
    }
    const n = parseFloat(String(focusedWalletAsset.availableAmount || "0"));
    const formattedAmount = BUSINESS_SUITE_CURRENCY_FORMATTER.format(
      n,
      "crypto",
      code,
      { showSymbol: false }
    );
    return `${formattedAmount} ${code}`;
  }, [focusedWalletAsset, settings?.digitalWallet?.showBalances]);

  const handleCopyAssetHero = async () => {
    if (!focusedWalletAsset) {
      return;
    }
    const text = assetFocusPrimaryLine.trim();
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast({ description: "Copied to clipboard" });
    } catch {
      toast({
        description: "Could not copy",
        variant: "destructive",
      });
    }
  };

  const renderQuoteCurrencyDropdown = (
    tone: "businessHero" | "assetFocus" = "businessHero"
  ) => (
    <div>
      {isLoadingAssets ? (
        <Skeleton className="w-20 h-10 rounded-full bg-accent/30" />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={
                tone === "assetFocus"
                  ? "text-white bg-transparent border-white/30 rounded-full hover:bg-white/10 hover:text-white h-[48px]"
                  : "text-white bg-transparent border-secondary/50 rounded-full hover:bg-background/30 hover:text-white h-[48px]"
              }
            >
              {selectedAsset?.asset?.toUpperCase() ||
                BUSINESS_SUITE_ZAR_ASSET_CODE}{" "}
              <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={8}
            className="rounded-lg max-h-80 overflow-y-auto"
          >
            {assetsForUse
              ?.filter((asset: WalletAsset) =>
                selectedAssetNames.includes(asset.assetName || "")
              )
              .map((asset: WalletAsset) => (
                <DropdownMenuItem asChild key={asset.id}>
                  <button
                    onClick={() => handleSelect(asset)}
                    className="w-full text-left flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent hover:border-rose-200"
                  >
                    <img
                      src={asset.image}
                      alt={asset.assetName}
                      className="w-6 h-6"
                    />
                    <div className="flex flex-col text-left">
                      <span>{asset.assetName}</span>
                      <span className="text-xs text-gray-500">
                        {asset.asset?.toUpperCase()}
                      </span>
                    </div>
                  </button>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  useEffect(() => {
    if (!isBusinessContextLoading && !activeBusiness) {
      navigate("/trading/digitalWallet", { replace: true });
    }
  }, [activeBusiness, isBusinessContextLoading, navigate]);

  if (!activeBusiness) {
    return null;
  }

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <div className="px-3 lg:px-14 py-6 bg-[#f8f8f8] rounded-t-2xl min-h-[calc(100vh-100px)]">
        <Toaster />
        <div className="flex flex-wrap lg:flex-nowrap lg:space-x-10 space-x-0">
          <main className="w-full space-y-6 mb-10">
            <>
              <div className="flex flex-wrap lg:flex-nowrap gap-6 w-full">
                <div className="w-full lg:w-[calc(58%-12px)] min-w-0">
                  <BusinessSuiteHero
                    isAssetFocusView={isAssetFocusView}
                    focusedWalletAsset={focusedWalletAsset}
                    assetFocusPrimaryLine={assetFocusPrimaryLine}
                    formattedPortfolioValue={formattedPortfolioValue}
                    showBalances={settings?.digitalWallet?.showBalances}
                    onCopyAssetHero={handleCopyAssetHero}
                    onToggleBalanceVisibility={handleToggleBalanceVisibility}
                    renderQuoteCurrencyDropdown={renderQuoteCurrencyDropdown}
                    activeBusiness={activeBusiness}
                  />
                </div>
                <div className="w-full lg:w-[calc(42%-12px)] min-w-0">
                  <CryptoAssetComponentDashboard
                    styleClasses="w-full lg:h-[441px] h-auto min-h-[320px]"
                    fromDashboard={false}
                    businessSuiteAssetNav
                    businessId={activeBusiness?.businessId ?? null}
                  />
                </div>
              </div>

              <div className="flex flex-wrap lg:flex-nowrap lg:space-x-10 space-x-0 ">
                <aside className="w-[30%] min-w-[302px] p-[24px] hidden lg:block">
                  <BusinessSuiteSideNav
                    activeTab={activeTab}
                    mainAccount={mainAccount}
                    onTabChange={handleTabChange}
                  />
                </aside>
                <div className="w-full space-y-4 mb-10">
                  {activeTab === "overview" && (
                    <>
                      {!isAssetFocusView && (
                        <BusinessSuitePerformanceCard
                          businessId={activeBusiness.businessId}
                        />
                      )}
                      <BusinessSuiteTransactionHistory
                        assetFocusView={isAssetFocusView}
                        businessId={activeBusiness.businessId}
                        onSplitPayment={handleSplitPaymentFromTransaction}
                      />
                    </>
                  )}

                  {activeTab === "dpos" && <DPOSPage />}

                  {activeTab === "market" && (
                    <MarketDashboard fromDashboard={false} />
                  )}

                  {activeTab === "buy-sell" &&
                    (featureFlags?.buyCrypto || featureFlags?.sellCrypto) &&
                    (isLoadingAssetsOwned ? (
                      <BuySellPageSkeleton />
                    ) : showBuySellCarousel ? (
                      <BuySellCarousel
                        onGetStarted={() => setShowBuySellCarousel(false)}
                      />
                    ) : (
                      <BuySellPage
                        businessId={activeBusiness.businessId}
                        assetToSelect={asset}
                        pairObject={pairObject}
                        tab={transactionTab}
                        quickActionTab={selectedTab}
                        childTab={childTab}
                        tradingAssets={tradingAssetsWithBusinessBalances}
                        zarBalance={zarBalance}
                      />
                    ))}

                  {activeTab === "send-receive" &&
                    (featureFlags?.sendCrypto ||
                      featureFlags?.receiveCrypto) && (
                      <SendReceivePage
                        businessId={activeBusiness.businessId}
                        assetToSelect={asset}
                        tab={transactionTab}
                        tradingNets={tradingNets}
                        tradingAssets={tradingAssetsWithBusinessBalances}
                        childTab={childTab}
                      />
                    )}

                  {activeTab === "withdraw-deposit" &&
                    (featureFlags?.deposit || featureFlags?.withdraw) && (
                      <WithdrawParent
                        businessId={activeBusiness.businessId}
                        tab={selectedTab}
                        childTab={childTab}
                      />
                    )}

                  {activeTab === "80eightpay" &&
                    mainAccount === true &&
                    featureFlags?.eightPay && (
                      <EightyEightPay
                        userId={userId}
                        initialPayId={urlPayId}
                        businessId={activeBusiness.businessId}
                      />
                    )}

                  {activeTab === "pay-link" &&
                    mainAccount === true &&
                    featureFlags?.eightPay && (
                      <PaymentLinksPanel
                        launchPayLinkWizard={launchPayLinkWizard}
                        splitPaymentLaunchContext={splitPaymentLaunchContext}
                        onLaunchPayLinkWizardConsumed={() => {
                          setLaunchPayLinkWizard(false);
                          setSplitPaymentLaunchContext(null);
                        }}
                      />
                    )}

                  {activeTab === "address-book" && (
                    <AddressBookPage parentTab="address-book" />
                  )}
                </div>
              </div>
            </>
          </main>
        </div>
      </div>
    </div>
  );
}
